import React, { useEffect, useState } from "react";
import { Modal, Form } from "react-bootstrap";
import PhoneInput from "react-phone-input-2";
import {
  FiUser,
  FiPhone,
  FiMail,
  FiMessageSquare,
  FiBriefcase,
  FiCheck,
  FiRefreshCw,
} from "react-icons/fi";
import "react-phone-input-2/lib/style.css";
import "./dmsBookingModal.css";

const initialFormData = {
  firstName: "",
  lastName: "",
  phone: "",
  country: "in",
  company: "",
  email: "",
  requirement: "",
  captchaInput: "",
};

const generateCaptcha = () => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let text = "";

  for (let i = 0; i < 6; i++) {
    text += chars.charAt(Math.floor(Math.random() * chars.length));
  }

  return text;
};

const DmsBookingModal = ({ show, onClose }) => {
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [captchaText, setCaptchaText] = useState(generateCaptcha());
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!show) {
      return;
    }

    const bodyOverflow = document.body.style.overflow;
    const htmlOverflow = document.documentElement.style.overflow;
    const bodyPaddingRight = document.body.style.paddingRight;

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    document.body.style.paddingRight = "0px";

    return () => {
      document.body.style.overflow = bodyOverflow;
      document.documentElement.style.overflow = htmlOverflow;
      document.body.style.paddingRight = bodyPaddingRight;
    };
  }, [show]);

  const handleClose = () => {
    setFormData(initialFormData);
    setErrors({});
    setCaptchaText(generateCaptcha());
    setSubmitted(false);
    setSubmitting(false);
    onClose();
  };

  useEffect(() => {
    if (!submitted) {
      return;
    }

    const timer = setTimeout(() => {
      handleClose();
    }, 5000);

    return () => clearTimeout(timer);
  }, [submitted]);

  const validateField = (field, value) => {
    let error = "";

    const nameRegex = /^[A-Za-z][A-Za-z ._-]*$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

    switch (field) {
      case "firstName":
        if (!value.trim()) {
          error = "First name is required";
        } else if (!nameRegex.test(value.trim())) {
          error = "Enter a valid first name";
        } else if (value.trim().length > 50) {
          error = "Maximum 50 characters allowed";
        }
        break;

      case "lastName":
        if (!value.trim()) {
          error = "Last name is required";
        } else if (!nameRegex.test(value.trim())) {
          error = "Enter a valid last name";
        } else if (value.trim().length > 50) {
          error = "Maximum 50 characters allowed";
        }
        break;

      case "company":
        if (!value.trim()) {
          error = "Company name is required";
        } else if (value.trim().length > 100) {
          error = "Maximum 100 characters allowed";
        }
        break;

      case "email":
        if (!value.trim()) {
          error = "Email is required";
        } else if (!emailRegex.test(value.trim())) {
          error = "Enter a valid email";
        }
        break;

      case "phone": {
        const digits = value.replace(/\D/g, "");

        if (!digits) {
          error = "Phone number is required";
        } else if (digits.length < 10) {
          error = "Enter a valid mobile number";
        }

        break;
      }

      case "requirement":
        if (!value.trim()) {
          error = "Requirement is required";
        } else if (value.trim().length > 500) {
          error = "Maximum 500 characters allowed";
        }
        break;

      case "captchaInput":
        if (!value.trim()) {
          error = "Enter CAPTCHA";
        } else if (value.trim().toUpperCase() !== captchaText) {
          error = "CAPTCHA does not match";
        }
        break;

      default:
        break;
    }

    return error;
  };

  const handleChange = (field, value) => {
    setFormData((previous) => ({
      ...previous,
      [field]: value,
    }));

    setErrors((previous) => ({
      ...previous,
      [field]: validateField(field, value),
    }));
  };

  const validateForm = () => {
    const fields = [
      "firstName",
      "lastName",
      "phone",
      "company",
      "email",
      "requirement",
      "captchaInput",
    ];

    const newErrors = {};

    fields.forEach((field) => {
      const error = validateField(field, formData[field]);

      if (error) {
        newErrors[field] = error;
      }
    });

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);

      const response = await fetch("https://formspree.io/f/xykzrwjb", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          formType: "DMS Demo Request",
          firstName: formData.firstName,
          lastName: formData.lastName,
          name: `${formData.firstName} ${formData.lastName}`,
          contactNumber: formData.phone,
          email: formData.email,
          company: formData.company,
          requirements: formData.requirement,
          captcha: formData.captchaInput,
        }),
      });

      if (!response.ok) {
        throw new Error("Form submission failed");
      }

      setSubmitted(true);
    } catch (error) {
      console.error("DMS booking submission failed:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const refreshCaptcha = () => {
    setCaptchaText(generateCaptcha());

    setFormData((previous) => ({
      ...previous,
      captchaInput: "",
    }));

    setErrors((previous) => ({
      ...previous,
      captchaInput: "",
    }));
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      size="lg"
      backdrop
      keyboard
      dialogClassName="dms-booking-modal-dialog"
      contentClassName="dms-booking-modal-content"
      enforceFocus
      restoreFocus
    >
      <Modal.Body className="dms-booking-modal-body">
        <button
          type="button"
          className="dms-modal-close"
          onClick={handleClose}
          aria-label="Close modal"
        >
          ×
        </button>

        <div className="dms-modal-layout">
          <div className="dms-modal-left">
            <div className="dms-modal-left-inner">
              <div className="dms-modal-brand">
                <span></span>
                TECHCOGNICS DMS
              </div>

              <h2>
                Smarter
                <br />
                Distribution.
                <br />
                <span>Faster Sales.</span>
              </h2>

              <p className="dms-modal-intro">
                See how Techcognics DMS can connect your distributors, sales
                teams, inventory, orders and retailers through one platform.
              </p>

              <div className="dms-modal-benefits">
                <div className="dms-modal-benefit">
                  <span>
                    <FiCheck />
                  </span>
                  <p>Distributor & Dealer Management</p>
                </div>

                <div className="dms-modal-benefit">
                  <span>
                    <FiCheck />
                  </span>
                  <p>Order & Inventory Management</p>
                </div>

                <div className="dms-modal-benefit">
                  <span>
                    <FiCheck />
                  </span>
                  <p>Primary & Secondary Sales</p>
                </div>

                <div className="dms-modal-benefit">
                  <span>
                    <FiCheck />
                  </span>
                  <p>Schemes, Claims & Collections</p>
                </div>

                <div className="dms-modal-benefit">
                  <span>
                    <FiCheck />
                  </span>
                  <p>Distribution Analytics & Reporting</p>
                </div>
              </div>

              <div className="dms-modal-flow">
                <span>Manufacturer</span>
                <i>→</i>
                <span>Distributor</span>
                <i>→</i>
                <span>Sales Team</span>
                <i>→</i>
                <span>Retailer</span>
              </div>
            </div>
          </div>

          <div className="dms-modal-right">
            <div className="dms-modal-header">
              <span>GET STARTED</span>

              <h3>Book a Free DMS Demo</h3>

              <p>
                Tell us about your business and our DMS expert will get in touch
                with you.
              </p>
            </div>

            {submitted ? (
              <div className="dms-thank-you">
                <div className="dms-thank-you-icon">
                  <FiCheck />
                </div>

                <h3>Thank you for reaching out!</h3>

                <p>
                  Your DMS demo request has been received. One of our experts
                  will get in touch with you shortly.
                </p>
              </div>
            ) : (
              <div className="dms-modal-form-scroll">
                <Form
                  id="dmsDemoForm"
                  onSubmit={handleSubmit}
                  className="dms-booking-form"
                >
                  <div className="row g-2">
                    <div className="col-md-6">
                      <Form.Group>
                        <div
                          className={`dms-input-group ${
                            errors.firstName ? "dms-input-error" : ""
                          }`}
                        >
                          <span>
                            <FiUser />
                          </span>

                          <Form.Control
                            type="text"
                            placeholder="First Name"
                            autoComplete="given-name"
                            value={formData.firstName}
                            onChange={(event) =>
                              handleChange("firstName", event.target.value)
                            }
                          />
                        </div>

                        {errors.firstName && (
                          <div className="dms-error">{errors.firstName}</div>
                        )}
                      </Form.Group>
                    </div>

                    <div className="col-md-6">
                      <Form.Group>
                        <div
                          className={`dms-input-group ${
                            errors.lastName ? "dms-input-error" : ""
                          }`}
                        >
                          <span>
                            <FiUser />
                          </span>

                          <Form.Control
                            type="text"
                            placeholder="Last Name"
                            autoComplete="family-name"
                            value={formData.lastName}
                            onChange={(event) =>
                              handleChange("lastName", event.target.value)
                            }
                          />
                        </div>

                        {errors.lastName && (
                          <div className="dms-error">{errors.lastName}</div>
                        )}
                      </Form.Group>
                    </div>
                  </div>

                  <Form.Group>
                    <div
                      className={`dms-input-group ${
                        errors.phone ? "dms-input-error" : ""
                      }`}
                    >
                      <span>
                        <FiPhone />
                      </span>

                      <PhoneInput
                        country={formData.country}
                        value={formData.phone}
                        onChange={(value, country) => {
                          setFormData((previous) => ({
                            ...previous,
                            phone: value,
                            country: country?.countryCode || "in",
                          }));

                          setErrors((previous) => ({
                            ...previous,
                            phone: validateField("phone", value),
                          }));
                        }}
                        countryCodeEditable={false}
                        enableSearch
                        specialLabel=""
                        inputProps={{
                          name: "phone",
                          autoComplete: "tel",
                          placeholder: "Phone number",
                        }}
                      />
                    </div>

                    {errors.phone && (
                      <div className="dms-error">{errors.phone}</div>
                    )}
                  </Form.Group>

                  <Form.Group>
                    <div
                      className={`dms-input-group ${
                        errors.company ? "dms-input-error" : ""
                      }`}
                    >
                      <span>
                        <FiBriefcase />
                      </span>

                      <Form.Control
                        type="text"
                        placeholder="Company Name"
                        autoComplete="organization"
                        value={formData.company}
                        onChange={(event) =>
                          handleChange("company", event.target.value)
                        }
                      />
                    </div>

                    {errors.company && (
                      <div className="dms-error">{errors.company}</div>
                    )}
                  </Form.Group>

                  <Form.Group>
                    <div
                      className={`dms-input-group ${
                        errors.email ? "dms-input-error" : ""
                      }`}
                    >
                      <span>
                        <FiMail />
                      </span>

                      <Form.Control
                        type="email"
                        placeholder="Business Email"
                        autoComplete="email"
                        value={formData.email}
                        onChange={(event) =>
                          handleChange("email", event.target.value)
                        }
                      />
                    </div>

                    {errors.email && (
                      <div className="dms-error">{errors.email}</div>
                    )}
                  </Form.Group>

                  <Form.Group>
                    <div
                      className={`dms-input-group dms-textarea-group ${
                        errors.requirement ? "dms-input-error" : ""
                      }`}
                    >
                      <span>
                        <FiMessageSquare />
                      </span>

                      <Form.Control
                        as="textarea"
                        rows={3}
                        placeholder="Tell us about your distribution requirements"
                        value={formData.requirement}
                        onChange={(event) =>
                          handleChange("requirement", event.target.value)
                        }
                      />
                    </div>

                    {errors.requirement && (
                      <div className="dms-error">{errors.requirement}</div>
                    )}
                  </Form.Group>

                  <div className="dms-captcha-section">
                    <div className="dms-captcha-header">
                      <span>Security Verification</span>

                      <button
                        type="button"
                        onClick={refreshCaptcha}
                        aria-label="Refresh CAPTCHA"
                      >
                        <FiRefreshCw />
                      </button>
                    </div>

                    <div className="row g-2 align-items-center">
                      <div className="col-5">
                        <div className="dms-captcha-box">{captchaText}</div>
                      </div>

                      <div className="col-7">
                        <Form.Control
                          type="text"
                          placeholder="Enter CAPTCHA"
                          value={formData.captchaInput}
                          onChange={(event) =>
                            handleChange("captchaInput", event.target.value)
                          }
                        />
                      </div>
                    </div>

                    {errors.captchaInput && (
                      <div className="dms-error">{errors.captchaInput}</div>
                    )}
                  </div>

                  <button
                    type="submit"
                    className="dms-submit-btn"
                    disabled={submitting}
                  >
                    {submitting ? "Submitting..." : "Book My DMS Demo"}

                    {!submitting && <span>→</span>}
                  </button>
                </Form>
              </div>
            )}
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default DmsBookingModal;
